<template>
    <ValidatorMessage
        :message="validateMessage"
        class="border"
    >
        <div class="form-control-phone input-group flex-nowrap">
            <DropdownSelect
                class="flex-shrink-1 border border-start-0 border-top-0 border-bottom-0"
                :data="computedCountryCodes"
                v-model="countrycode"
                :favorites="['+47', '+46', '+358', '+49', '+39', '+33']"
                :showSearch="true"
            />
            <InputMask
                v-if="inputMaskData != null && !isObfuscated"
                v-model="phonenumber"
                ref="inputref"
                class="text-end font-monospace text-lowercase flex-fill"
                :style="{ 'margin-end': '1px' }"
                @mask-value-changed="maskValueChanged"
                @blur="onMaskBlur"
                :mask-data="inputMaskData as InputMaskData"
            />
            <InputText
                v-else
                @keydown="filterKeys"
                @keyup="checkIfEmpty"
                @focus="onInputFocus"
                v-model="phonenumber"
                class="text-end font-monospace text-lowercase flex-fill border-0"
            />
            <span
                class="input-group-text keep-icon border border-top-0 border-end-0 border-bottom-0"
                @click="callNumber"
            >
                <IconComp icon="fa-phone" />
            </span>
        </div>
    </ValidatorMessage>
</template>
<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch, type ComponentPublicInstance } from 'vue';
import type { IIcon } from '../Interfaces';
import { type IEditorComponent } from '@/objects/gui';
import { useLoaders, useTaskLoader } from '@/composeables';
import { DropdownSelect, IconComp, InputMask, ValidatorMessage } from '@/components';
import { useEventCommonStoreRef, useCommonStoreRef } from '@/stores';
import { type IAlertMessage, type SelectLine, type SelectLineValueType } from '@/objects';
import InputMaskData from '@/objects/InputMaskData';
import { $tm } from '@/locize';
import InputText from './InputText.vue';
import { isCommonKey } from '@/utilities/keyboardutils';

defineOptions({ inheritAttrs: false });

const props = withDefaults(
    defineProps<
        {
            modelValue: string | null;

            /**
             * Should we autofocus the input
             */
            autofocus?: boolean;

            /**
             * If the input is readonly
             */
            readonly?: boolean;

            /**
             * If the input is disabled
             */
            disabled?: boolean;
        } & IIcon
    >(),
    {},
);

// We preload the needed countries
const { Event } = useEventCommonStoreRef();
const { Countries } = useCommonStoreRef();
const { AddLoader } = useTaskLoader();
useLoaders(AddLoader, ['COUNTRIES']);

// The ref of the input element
const inputref = ref<ComponentPublicInstance<IEditorComponent> | null>(null);

// Contains the original value of the modelvalue
const originalValue = props.modelValue;

/**
 * Get the phonenumber and the country code from the modelvalue
 */
const getPhonenumberAndCode = () => {
    // We need to find out if the phonenumber has a country code If it doesnt we just set the dropdown to "empty"
    let _pn = props.modelValue?.toString();
    let countrycode = null;
    let mask = null;

    if (_pn) {
        // if the phonenumber starts with + or 00 we can assume it has a country code and we need to find that from the country list
        if (_pn.startsWith('+') || _pn.startsWith('00')) {
            // We normalize it so it starts with + instead of 00
            if (_pn.startsWith('00')) {
                _pn = '+' + _pn.substring(2);
            }
            // We need to loop throug all the computedCountries and check if the phonenumber starts with the dialcode
            for (let i = 0; i < Countries.value.length; i++) {
                if (!Countries.value[i].DialCode?.startsWith('+')) {
                    continue;
                }
                if (_pn.startsWith(Countries.value[i].DialCode as string)) {
                    // We return the country code and the phonenumber without the country code
                    let countrycode = Countries.value[i].DialCode;
                    let values = {
                        code: countrycode?.trim(),
                        phonenumber: _pn.replace(countrycode ?? '', '')?.trim(),
                        mask: Countries.value[i].DialMask?.replace(countrycode ?? '', '')?.trim(),
                    };

                    // if the dialmask starts with a - or a space we remove that
                    if (values.mask?.startsWith('-') || values.mask?.startsWith(' ')) {
                        values.mask = values.mask.substring(1);
                    }

                    return values;
                }
            }
        }
    }
    // We try and find the countrycode from the Event.Organizer
    let organizerCountryID = Event.value?.Organizer?.CountryID;
    let country = Countries.value.find((c) => c.ID == organizerCountryID);
    countrycode = country?.DialCode;
    mask = country?.DialMask?.replace(countrycode ?? '', '')?.trim();

    return {
        code: countrycode,
        phonenumber: _pn?.trim(),
        mask: mask,
    };
};

/**
 * Get the country by the dialcode
 * @param code
 */
const getCountryByCode = (code: string) => {
    const country = Countries.value.find((c) => c.DialCode == code);

    // We remove the countrycode from the mask
    if (country?.DialMask) {
        country.DialMask = country.DialMask.replace(code, '');

        // Remove if the dialmask starts with a - or a space
        if (country.DialMask.startsWith('-') || country.DialMask.startsWith(' ')) {
            country.DialMask = country.DialMask.substring(1);
        }
    }
    return country;
};

// We parse the phone number and the country code from the modelvalue
const { code: parsedCountryCode, phonenumber: parsedPhoneNumber, mask: phoneInputMask } = getPhonenumberAndCode();

/**
 * Helper to check if the current value is obfuscated. We check the obfuscater by looking for the ***** in the value
 */

const isObfuscated = computed(() => {
    return props.modelValue != null && props.modelValue.includes('*****');
});

// The inputmaskdata that we want to use. This is reactive and will be changed whenever the countrycode changes
const inputMaskData = ref<InputMaskData | null>(
    (phoneInputMask ?? '').length == 0 ? null : InputMaskData.CreateFromPhoneMask(phoneInputMask!),
);

// The internale phonenumber and countrycode
const phonenumber = ref<string>(parsedPhoneNumber ?? '');
const countrycode = ref<SelectLineValueType | null>(parsedCountryCode ?? '');

/**
 * Emit the modelvalue whenever the phonenumber changes
 */
const emit = defineEmits<{
    (e: 'update:modelValue', modelValue: string | null): void;
}>();

/**
 * The list of country codes for the dropdown
 */
const computedCountryCodes = computed(() => {
    // Map countries to selectlines and run translate on them and then order them alphabetically
    let countries = Countries.value
        .filter((c) => !!c.DialCode)
        .map((c) => {
            return {
                value: c.DialCode,
                text: `${c.DialCode}`,
                subtext: $tm(c.TranslationKey ?? ''),
                icon: `fi-${c.ISO2?.toLowerCase()}`,
            } as SelectLine;
        })
        .sort((a, b) => (a.subtext ?? '').localeCompare(b.subtext ?? ''));

    return countries;
});

/**
 * The validate message that we will show if the phonenumber is invalid
 */
const validateMessage = ref<IAlertMessage | null>(null);

/**
 * We check whenever anything changes and then we validate it and emit a empty modelvalue
 * @param value
 */
const maskValueChanged = (value: string) => {
    emitModelValue();
};

/**
 * Validate and emit the modelvalue whenever the mask loses focus
 */
const onMaskBlur = (emitValue = true) => {
    const number = (
        inputMaskData.value != null ? inputMaskData.value?.getCurrentDisplayText()?.replace(/#/g, '') : phonenumber.value
    )?.trim();

    const isValid = inputMaskData.value != null ? inputMaskData.value?.isMaskFilled() : true;
    if (!isObfuscated.value && !isValid && number.trim() != '') {
        validateMessage.value = {
            type: 'danger',
            message: 'fieldvalidate.invalidphonenumber',
        };
    } else {
        validateMessage.value = null;
    }

    if (emitValue) {
        emitModelValue();
    }
};

/**
 * Get the valid number as raw with the country code in front
 */
const getValidNumber = () => {
    let number = (inputMaskData.value != null ? inputMaskData.value?.getCurrentDisplayText() : phonenumber.value)?.trim();
    let isValid = inputMaskData.value != null ? inputMaskData.value?.isMaskFilled() : true;
    let code = number == '' ? '' : countrycode.value;

    if (!isValid && number != '') {
        return null;
    } else {
        return `${code}${number}`;
    }
};

/**
 * Emit the modelvalue
 */
const emitModelValue = () => {
    let number = (inputMaskData.value != null ? inputMaskData.value?.getCurrentDisplayText() : phonenumber.value)?.trim();
    const isValid = inputMaskData.value != null ? inputMaskData.value?.isMaskFilled() : true;

    if (!isValid && number != '') {
        emit('update:modelValue', originalValue == '' ? '' : null); // We make sure we return the same empty value as we started with.
    } else {
        let code = number == '' ? '' : countrycode.value;
        // Remove all the empty spaces
        let emitNumber = `${code}${number}`.replace(/ /g, '');
        emit('update:modelValue', emitNumber);
    }
};

/**
 * When we are clearing the field, we check when it is empty and then we emit the modelvalue before we blur.
 * This is so the user sees that the value is valid before he leaves the field
 */
const checkIfEmpty = () => {
    const currentDisplayText = inputMaskData.value?.getCurrentDisplayText() ?? '';
    if (currentDisplayText.trim() == '') {
        onMaskBlur();
    }
};

/**
 * Call the number
 */
const callNumber = () => {
    let number = getValidNumber();
    if (number) {
        window.open(`tel:${number}`);
    }
};

/**
 * Make sure we can only input numbers
 * @param e
 */
const filterKeys = (e: KeyboardEvent) => {
    // If the key is not a number or a comma or a dot, we prevent the default
    if (!/[\d]/.test(e.key) && !isCommonKey(e)) {
        e.preventDefault();
    }
};

let origianlObfuscatedValue: string | null = null;

/**
 * When we focus on the regular input we want to check if the value is obfuscated. If it is, we will try and empty the value and switch to the mask input for proper input
 */
const onInputFocus = () => {
    if (!isObfuscated.value) {
        return;
    }

    if (origianlObfuscatedValue == null) {
        origianlObfuscatedValue = phonenumber.value;
    }

    // We just emit a empty modelvalue and clear the phonenumber
    phonenumber.value = '';
    emit('update:modelValue', null);

    // Set the focus to the input after a short delay so the input is ready
    nextTick(() => inputref.value?.focus());
};

/**
 * Check if the phonenumber is valid and then emit the modelvalue
 */
watch(phonenumber, () => emitModelValue());

/**
 * Check and update the mask and the modelvalue whenever the countrycode changes
 */
watch(countrycode, (nv) => {
    // We need to update the mask

    let country = getCountryByCode(nv as string);
    inputMaskData.value = (country?.DialMask ?? '').length == 0 ? null : InputMaskData.CreateFromPhoneMask(country?.DialMask ?? '');

    emitModelValue();
});

onMounted(() => {
    // We need to focus the input if autofocus is set
    if (props.autofocus) {
        inputref.value?.focus();
    }

    // We trigger the mask blur so we can mark if it is a invalid number
    // This will help us remove invalid numbers from the database, since most will just remove the number when it is marked as invalid and save again
    // But we do NOT want to emit the value since that will set the value to null if it is invalid
    onMaskBlur(false);
});

defineExpose<IEditorComponent>({ focus: () => inputref.value?.focus(), validateMessage: validateMessage });
</script>
